/**
 * Scheduled Bridge Service
 *
 * Bridges the gap between auto-detected recurring patterns
 * and scheduled transactions. When detection runs, this service
 * converts detected bills/income into scheduled transactions.
 *
 * Key concept: Opt-out instead of opt-in. If detected, it's scheduled.
 */

import { db, ScheduleFrequency } from './db';
import { scheduledTransactionService } from './scheduledTransactions';
import { DetectedBill, DetectedIncome } from '@hooks/useRecurring';

// ============================================================
// Debug
// ============================================================

const DEBUG =
  import.meta.env.VITE_DEBUG === 'true' ||
  (typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debug'));

function log(...args: unknown[]): void {
  if (DEBUG) {
    console.log('[BRIDGE]', ...args);
  }
}

// ============================================================
// Bridge Cooldown (RECURRING-FIX: Issue 5)
// ============================================================

/**
 * Cooldown mechanism to prevent race condition where detection runs
 * before dismissal/delete syncs to other devices.
 *
 * When user dismisses a recurring item, we set a cooldown.
 * Bridge won't create new schedules until cooldown expires.
 * Per GPT-5.2: 15 seconds is responsive but safe.
 */
const BRIDGE_COOLDOWN_KEY = 'ninjacash-bridge-cooldown';
const BRIDGE_COOLDOWN_MS = 15000; // 15 seconds

/**
 * Check if bridge is in cooldown period.
 */
function isInCooldown(): boolean {
  try {
    const cooldownUntil = localStorage.getItem(BRIDGE_COOLDOWN_KEY);
    if (!cooldownUntil) return false;
    return Date.now() < parseInt(cooldownUntil, 10);
  } catch {
    return false;
  }
}

/**
 * Set bridge cooldown. Call this when user dismisses/stops a recurring item.
 * Exported for use by scheduledRunner.ts.
 *
 * @param seconds - Cooldown duration (default: 15 seconds)
 */
export function setBridgeCooldown(seconds: number = BRIDGE_COOLDOWN_MS / 1000): void {
  try {
    const cooldownUntil = Date.now() + seconds * 1000;
    localStorage.setItem(BRIDGE_COOLDOWN_KEY, String(cooldownUntil));
    log('[COOLDOWN] Set bridge cooldown for', seconds, 'seconds');
  } catch {
    // localStorage not available
  }
}

/**
 * Clear bridge cooldown. Call this after sync completes successfully.
 */
export function clearBridgeCooldown(): void {
  try {
    localStorage.removeItem(BRIDGE_COOLDOWN_KEY);
    log('[COOLDOWN] Cleared bridge cooldown');
  } catch {
    // localStorage not available
  }
}

// ============================================================
// Detection Key Normalization
// ============================================================

/**
 * Normalize a payee/description to create a consistent detection key.
 * This is used to match detected patterns with existing schedules.
 */
export function normalizeDetectionKey(description: string): string {
  return description
    .toLowerCase()
    .trim()
    .replace(/[#*@]+/g, '')
    .replace(/\s+/g, ' ')
    .substring(0, 100); // Limit length
}

// ============================================================
// Schedule Conversion
// ============================================================

/**
 * Parse day of month from a string like "15th", "1st-5th", "Various"
 */
function parseDayOfMonth(dayStr: string): number | undefined {
  // Try to extract first number
  const match = dayStr.match(/(\d+)/);
  if (match) {
    const day = parseInt(match[1], 10);
    if (day >= 1 && day <= 31) {
      return day;
    }
  }
  return undefined;
}

/**
 * Parse schedule from detected pattern string.
 * Returns frequency and optional week/month info.
 */
function parseSchedule(scheduleStr: string): {
  frequency: ScheduleFrequency;
  dayOfWeek?: number;
} {
  const lower = scheduleStr.toLowerCase();

  if (lower.includes('weekly') && !lower.includes('biweekly')) {
    return { frequency: 'weekly' };
  }
  if (lower.includes('biweekly') || lower.includes('2 weeks') || lower.includes('every 2')) {
    return { frequency: 'biweekly' };
  }
  if (lower.includes('yearly') || lower.includes('annual')) {
    return { frequency: 'yearly' };
  }

  // Default to monthly
  return { frequency: 'monthly' };
}

/**
 * Calculate next due date based on detected pattern.
 *
 * IMPORTANT: Only advances ONCE from the last payment date.
 * If the next due date is in the past, it will show as OVERDUE.
 * The runner advances to the next occurrence when user "pays" it.
 *
 * Example: Last payment Oct 17, today Dec 28
 * - nextDueDate = Nov 17 (OVERDUE, not Jan 17)
 */
function calculateInitialNextDueDate(
  lastDate: Date,
  frequency: ScheduleFrequency,
  dayOfMonth?: number
): Date {
  // Start from last known date
  let nextDate = new Date(lastDate);

  // For monthly, set to the expected day
  if (frequency === 'monthly' && dayOfMonth) {
    nextDate.setDate(dayOfMonth);
  }

  // Advance ONCE to get the next occurrence after last payment
  // Don't keep advancing to future - if it's in the past, it's OVERDUE
  switch (frequency) {
    case 'weekly':
      nextDate.setDate(nextDate.getDate() + 7);
      break;
    case 'biweekly':
      nextDate.setDate(nextDate.getDate() + 14);
      break;
    case 'monthly':
      nextDate.setMonth(nextDate.getMonth() + 1);
      if (dayOfMonth) {
        nextDate.setDate(dayOfMonth);
      }
      break;
    case 'yearly':
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      break;
  }

  return nextDate;
}

// ============================================================
// Bridge Functions
// ============================================================

/**
 * Convert a detected bill to a scheduled transaction.
 * Does NOT create if already exists or ignored.
 */
export async function bridgeDetectedBill(bill: DetectedBill): Promise<string | null> {
  const detectionKey = normalizeDetectionKey(bill.name);

  // Check if already scheduled or ignored
  const existing = await scheduledTransactionService.findByDetectionKey(detectionKey);

  if (existing) {
    if (existing.isIgnoredPermanently) {
      log('[SKIP] Bill ignored permanently:', bill.name);
      return null;
    }
    if (existing.isPaused && existing.pausedUntil) {
      const now = new Date();
      if (existing.pausedUntil > now) {
        log('[SKIP] Bill paused until:', existing.pausedUntil, bill.name);
        return null;
      }
      // Pause period ended - resume by clearing pause
      await scheduledTransactionService.resume(existing.id);
      log('[RESUME] Bill pause ended:', bill.name);
    }
    if (!existing.deletedAt) {
      log('[SKIP] Bill already scheduled:', bill.name);
      return existing.id;
    }
    // BUG-037 FIX: If deletedAt is set, user dismissed this pattern - don't recreate
    log('[SKIP] Bill was dismissed by user:', bill.name);
    return null;
  }

  // Parse schedule info
  const dayOfMonth = parseDayOfMonth(bill.dayOfMonth);

  // Create scheduled transaction
  const id = await scheduledTransactionService.create({
    accountId: bill.account,
    type: 'expense',
    amount: bill.averageAmount,
    description: bill.name,
    frequency: 'monthly',
    dayOfMonth,
    nextDueDate: calculateInitialNextDueDate(bill.lastDate, 'monthly', dayOfMonth),
    isPaused: false,
    isIgnoredPermanently: false,
    autoDetected: true,
    detectionKey,
    dismissCount: 0,
  });

  log('[OK] Created scheduled bill:', bill.name, 'id:', id);
  return id;
}

/**
 * Convert a detected income to a scheduled transaction.
 * Does NOT create if already exists or ignored.
 */
export async function bridgeDetectedIncome(income: DetectedIncome): Promise<string | null> {
  const detectionKey = normalizeDetectionKey(income.name);

  // Check if already scheduled or ignored
  const existing = await scheduledTransactionService.findByDetectionKey(detectionKey);

  if (existing) {
    if (existing.isIgnoredPermanently) {
      log('[SKIP] Income ignored permanently:', income.name);
      return null;
    }
    if (existing.isPaused && existing.pausedUntil) {
      const now = new Date();
      if (existing.pausedUntil > now) {
        log('[SKIP] Income paused until:', existing.pausedUntil, income.name);
        return null;
      }
      // Pause period ended - resume
      await scheduledTransactionService.resume(existing.id);
      log('[RESUME] Income pause ended:', income.name);
    }
    if (!existing.deletedAt) {
      log('[SKIP] Income already scheduled:', income.name);
      return existing.id;
    }
    // BUG-037 FIX: If deletedAt is set, user dismissed this pattern - don't recreate
    log('[SKIP] Income was dismissed by user:', income.name);
    return null;
  }

  // Parse schedule from detected pattern
  const { frequency, dayOfWeek } = parseSchedule(income.schedule);

  // Get account ID from first matching transaction
  // (Income detection doesn't include account - we need to find it)
  const accountId = await findAccountForIncome(income);
  if (!accountId) {
    log('[WARN] No account found for income:', income.name);
    return null;
  }

  // Create scheduled transaction
  const id = await scheduledTransactionService.create({
    accountId,
    type: 'income',
    amount: income.averageAmount,
    description: income.name,
    frequency,
    dayOfWeek,
    nextDueDate: calculateInitialNextDueDate(income.lastDate, frequency),
    isPaused: false,
    isIgnoredPermanently: false,
    autoDetected: true,
    detectionKey,
    dismissCount: 0,
  });

  log('[OK] Created scheduled income:', income.name, 'id:', id);
  return id;
}

/**
 * Find the account ID for a detected income pattern.
 * Searches recent transactions matching the description.
 */
async function findAccountForIncome(income: DetectedIncome): Promise<string | undefined> {
  try {
    const normalizedName = income.name.toLowerCase().trim();

    // Search recent transactions for this income
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    let transactions;
    try {
      transactions = await db.transactions
        .where('type')
        .equals('income')
        .filter((t) => {
          if (t.deletedAt) return false;
          const desc = t.description.toLowerCase().trim();
          // Check for exact match or contains
          return desc === normalizedName || desc.includes(normalizedName) || normalizedName.includes(desc);
        })
        .toArray();
    } catch (err) {
      // Fallback to full scan if index doesn't exist
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[BRIDGE] type index not found, falling back to full scan');
        const all = await db.transactions.toArray();
        transactions = all.filter((t) => {
          if (t.deletedAt) return false;
          if (t.type !== 'income') return false;
          const desc = t.description.toLowerCase().trim();
          return desc === normalizedName || desc.includes(normalizedName) || normalizedName.includes(desc);
        });
      } else {
        throw err;
      }
    }

    if (transactions.length > 0) {
      // Return most recent transaction's account
      transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      return transactions[0].accountId;
    }

    // Fallback: get first active account
    const accounts = await db.accounts.filter((a) => a.isActive).toArray();
    return accounts.length > 0 ? accounts[0].id : undefined;
  } catch (err) {
    console.error('[BRIDGE] Error finding account for income:', err);
    return undefined;
  }
}

/**
 * Bridge all detected patterns to scheduled transactions.
 * Called after recurring detection runs.
 *
 * IMPORTANT: This function catches all errors to prevent crashes during
 * the data event flow. If scheduled tables don't exist, it fails silently.
 *
 * RECURRING-FIX: Issue 5A - Checks cooldown before bridging.
 * If user recently dismissed an item, skip bridging to let sync propagate first.
 */
export async function bridgeAllDetected(
  income: DetectedIncome[],
  bills: DetectedBill[]
): Promise<{ incomeCreated: number; billsCreated: number }> {
  try {
    // RECURRING-FIX: Issue 5A - Skip if in cooldown period
    if (isInCooldown()) {
      log('[COOLDOWN] Bridge skipped - in cooldown period after dismiss');
      return { incomeCreated: 0, billsCreated: 0 };
    }

    console.log('[SCHEDULED-BRIDGE] Detected patterns:', income.length, 'income,', bills.length, 'bills');

    let incomeCreated = 0;
    let billsCreated = 0;

    // Bridge income
    for (const inc of income) {
      try {
        const id = await bridgeDetectedIncome(inc);
        if (id) {
          // Check if this was a new creation (not existing)
          const existing = await scheduledTransactionService.findByDetectionKey(
            normalizeDetectionKey(inc.name)
          );
          if (existing && existing.id === id) {
            incomeCreated++;
          }
        }
      } catch (err) {
        // Log but don't throw - continue with other items
        console.warn('[BRIDGE] Failed to bridge income:', inc.name, err);
      }
    }

    // Bridge bills
    for (const bill of bills) {
      try {
        const id = await bridgeDetectedBill(bill);
        if (id) {
          // Check if this was a new creation
          const existing = await scheduledTransactionService.findByDetectionKey(
            normalizeDetectionKey(bill.name)
          );
          if (existing && existing.id === id) {
            billsCreated++;
          }
        }
      } catch (err) {
        // Log but don't throw - continue with other items
        console.warn('[BRIDGE] Failed to bridge bill:', bill.name, err);
      }
    }

    if (incomeCreated > 0 || billsCreated > 0) {
      console.log('[SCHEDULED-BRIDGE] Created schedules:', incomeCreated, 'income,', billsCreated, 'bills');
    }
    return { incomeCreated, billsCreated };
  } catch (err) {
    // Catch-all to prevent errors from propagating to React components
    console.error('[BRIDGE] bridgeAllDetected failed:', err);
    return { incomeCreated: 0, billsCreated: 0 };
  }
}

/**
 * Update amount for an existing schedule based on new detection.
 * Only updates if the new amount is significantly different.
 */
export async function updateScheduleFromDetection(
  scheduleId: string,
  newAmount: number
): Promise<void> {
  const schedule = await scheduledTransactionService.getById(scheduleId);
  if (!schedule) return;

  // Only update if difference is more than 10%
  const diff = Math.abs(schedule.amount - newAmount) / schedule.amount;
  if (diff > 0.1) {
    await scheduledTransactionService.update(scheduleId, { amount: newAmount });
    log('[OK] Updated schedule amount:', schedule.description, schedule.amount, '->', newAmount);
  }
}
